import React, {useEffect, useMemo, FC, ComponentProps} from "react"
import DateTimePicker from "react-datetime-picker"
import DatePicker from "react-date-picker"
import TimePicker from "react-time-picker"
import classnames from "classnames"
import {CalendarProps} from 'react-calendar'
import 'react-datetime-picker/dist/DateTimePicker.css'
import 'react-date-picker/dist/DatePicker.css'
import 'react-time-picker/dist/TimePicker.css'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'

import './multi-behaviour-datetime.scss'
import {
  getClassesAndProps,
  getDatetimePartsForResponseFromDatetimePickerResponse
} from "./index.func"

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface DateTimeShapeProps {
  type: "date" | "time" | "date_time";
  value: "hour_minute" | "month_day" | "year" | "year_month" | "year_month_day" | "day_hour_minute" | "year_month_day_hour_minute";
}

interface MultiBehaviourDatetimePickerProps extends ComponentProps<typeof DateTimePicker> {
  value: Value;
  onChangeState?: (value: {
    year: string | null;
    month: string | null;
    day: string | null;
    hour: string | null;
    minute: string | null
  }) => void;
  onChange?: (value: Value) => void;
  datetimeShape: DateTimeShapeProps;
}

// Define the additional CalendarProps type
interface CustomCalendarProps extends CalendarProps {
  format: string;
  hideMonth: boolean;
  hideDayNames: boolean;
  hideBoarder?: boolean;
}

export const MultiBehaviourDatetimePicker: FC<MultiBehaviourDatetimePickerProps> = React.forwardRef(({ value, onChangeState, onChange, className = 'form-control', datetimeShape }, ref) => {
  // Generate a unique ID only once per component mount
  const id = useMemo(() => Math.random().toString(36), [])

  const datetimePickerDetails = getClassesAndProps(datetimeShape)

  useEffect(() => {
    if (onChangeState) {
      const dateParts = getDatetimePartsForResponseFromDatetimePickerResponse({
        datetimePickerDetails,
        datetimeShape
      }, value)
      onChangeState(dateParts)
    }
  }, [value])

  if (datetimeShape.type === 'date') {

    return (
      <DatePicker
        id={`date_${id}`}
        value={value}
        onChange={onChange}
        calendarProps={{
          minDetail: datetimePickerDetails.minDetail,
          maxDetail: datetimePickerDetails.maxDetail,
          minDate: datetimePickerDetails.minDate,
          maxDate: datetimePickerDetails.maxDate,
          formatMonthYear: datetimePickerDetails.formatMonthYear,
          formatYear: datetimePickerDetails.formatYear,
          defaultActiveStartDate: value instanceof Date ? value : datetimePickerDetails.defaultActiveStartDate
        }}
        className={classnames(`${className} datetime-picker-container`, {
          'hide-day-names': datetimePickerDetails.hideDayNames,
          'hide-month': datetimePickerDetails.hideMonth
        })}
        format={datetimePickerDetails.format}
        clearIcon={null}
      />
    )
  }

  if (datetimeShape.type === 'time') {
    return (
      <TimePicker
        id={`time_${id}`}
        value={value}
        // @ts-ignore
        onChange={onChange}
        shouldOpenClock={() => false}
        clockIcon={null}
        format={'HH:mm'}
        className={classnames(`${className} datetime-picker-container`)}
        clearIcon={null}
      />
    )
  }

  return (
    <DateTimePicker
      id={`datetime_${id}`}
      value={value}
      onChange={onChange}
      disableClock={true}
      calendarProps={{
        minDetail: datetimePickerDetails.minDetail,
        maxDetail: datetimePickerDetails.maxDetail,
        minDate: datetimePickerDetails.minDate,
        maxDate: datetimePickerDetails.maxDate,
        formatMonthYear: datetimePickerDetails.formatMonthYear,
        defaultActiveStartDate: datetimePickerDetails.defaultActiveStartDate
      }}
      className={classnames(`${className} datetime-picker-container`, {
        'hide-day-names': datetimePickerDetails.hideDayNames,
        'hide-month': datetimePickerDetails.hideMonth
      })}
      format={datetimePickerDetails.format}
      calendarIcon={null}
      clearIcon={null}
    />
  )
})

export type {CustomCalendarProps, DateTimeShapeProps}
